import React from 'react';
import { Box, Card, Icon, Link, Text, Tooltip } from '@nimbus-ds/components';
import { ExternalLinkIcon, InfoCircleIcon } from '@nimbus-ds/icons';
import { copyToClipboard } from '@tiendanube/nexo';
import nexo from 'App/nexoClient';
import { useFeatureFlag } from 'commons/hooks';
import { FeatureFlag } from 'commons/services';
import { ChargebackReason } from 'commons/types';
import { useTranslationWithPrefix } from 'commons/utils';
import { getDefenseModelUrl } from '../utils/defenseModelUrl';
import DefenseInfoSkeleton from './DefenseInfoSkeleton';
import { useDefenseModelUrl } from './useDefenseModelUrl';

interface DefenseInfoProps {
  orderId: string;
  orderNumber: number;
  chargebackReason: ChargebackReason;
  providerCaseId?: string;
}

function DefenseInfo({
  orderId,
  orderNumber,
  chargebackReason,
  providerCaseId,
}: DefenseInfoProps) {
  const { t } = useTranslationWithPrefix('chargebackUpload.defenseInfo');
  const { fetchUrl, loading } = useDefenseModelUrl(orderId);
  const { enabled: isSignedUrlEnabled } = useFeatureFlag(
    FeatureFlag.FS_NUVEMPAGO_DEFENSE_MODEL,
  );
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    await copyToClipboard(nexo, providerCaseId!);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card data-testid="chargeback-defense-info">
      <Card.Header title={t('title', { orderNumber })} />
      <Card.Body>
        <Box display="flex" flexDirection="column" gap="2">
          <Box display="flex" flexDirection="row" gap="1" alignItems="center">
            <Text color="neutral-textLow">{t('reason')}</Text>
            <Text fontWeight="medium">
              {t(`reasonValue.${chargebackReason}`)}
            </Text>
            <Tooltip
              content={t(`reasonTooltip.${chargebackReason}`)}
              position="top"
            >
              <Icon
                color="neutral-textLow"
                source={<InfoCircleIcon size="small" />}
              />
            </Tooltip>
          </Box>

          {providerCaseId && (
            <Box
              display="flex"
              flexDirection="row"
              gap="1"
              alignItems="center"
              flexWrap="wrap"
            >
              <Text color="neutral-textLow">{t('defenseNumber')}</Text>
              <Text fontWeight="medium">
                {providerCaseId.length > 20
                  ? `${providerCaseId.substring(0, 20)}...`
                  : providerCaseId}
              </Text>
              <Link
                as="button"
                appearance="primary"
                textDecoration="none"
                onClick={handleCopy}
              >
                {copied ? t('copied') : t('copy')}
              </Link>
            </Box>
          )}

          {isSignedUrlEnabled ? (
            <Link
              as="button"
              onClick={fetchUrl}
              disabled={loading}
              appearance="primary"
              textDecoration="none"
            >
              {t('defenseModelLink')}
              <Icon
                source={<ExternalLinkIcon size="small" />}
                color="currentColor"
              />
            </Link>
          ) : (
            <Link
              as="a"
              href={getDefenseModelUrl(chargebackReason)}
              target="_blank"
              rel="noopener noreferrer"
              appearance="primary"
              textDecoration="none"
            >
              {t('defenseModelLink')}
              <Icon
                source={<ExternalLinkIcon size="small" />}
                color="currentColor"
              />
            </Link>
          )}
        </Box>
      </Card.Body>
    </Card>
  );
}

DefenseInfo.Skeleton = DefenseInfoSkeleton;
export default DefenseInfo;
